'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "messageId", "index", "items", "groupKey", "slots", "slotProps"];
import * as React from 'react';
import { useStore } from '@mui/x-internals/store';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChatStore } from "../hooks/useChatStore.js";
import { useMessage, useMessageIds } from "../hooks/useMessage.js";
import { useMessageAuthor } from "../hooks/useMessageAuthor.js";
import { getMessageWithResolvedAuthor } from "../internals/messageAuthor.js";
import { useChatVariant } from "../chat/internals/ChatVariantContext.js";
import { useChatDensity } from "../chat/internals/ChatDensityContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { chatSelectors } from "../selectors/index.js";
import { MessageAvatar } from "../message/MessageAvatar.js";
import { MessageContent } from "../message/MessageContent.js";
import { MessageMeta } from "../message/MessageMeta.js";
import { MessageRoot } from "../message/MessageRoot.js";

/**
 * A function that maps a message to a group key.
 * Messages that resolve to the same key are visually grouped together
 * (shared avatar, author name, etc.).
 * @param {ChatMessage} message The message to derive a group key from.
 * @returns {string | number} The group key for the message.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_GROUP_KEY = message => message.author?.id ?? message.role ?? '';

/**
 * Creates a `groupKey` function that groups messages by author within a sliding
 * time window. Messages from the same author sent more than `windowMs` milliseconds
 * apart will start a new group.
 *
 * @param windowMs - The grouping window in milliseconds. Defaults to 300 000 (5 minutes).
 *
 * @example
 * // Group messages from the same author within a 1-minute window
 * <MessageGroup groupKey={createTimeWindowGroupKey(60_000)} messageId={id} />
 */
export function createTimeWindowGroupKey(windowMs = 300_000) {
  return message => {
    const timestamp = message.createdAt ? Date.parse(message.createdAt) : null;
    const bucket = timestamp != null && !Number.isNaN(timestamp) ? Math.floor(timestamp / windowMs) : 0;
    return `${message.author?.id ?? message.role ?? ''}-${bucket}`;
  };
}
function resolveMessageIndex(messageId, index, items) {
  if (index != null) {
    return index;
  }
  return items.indexOf(messageId);
}
export const MessageGroup = /*#__PURE__*/React.forwardRef(function MessageGroup(props, ref) {
  const {
      children,
      messageId,
      index,
      items: itemsProp,
      groupKey = DEFAULT_GROUP_KEY,
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const defaultItems = useMessageIds();
  const items = itemsProp ?? defaultItems;
  const store = useChatStore();
  const activeConversation = useStore(store, chatSelectors.activeConversation);
  const messageIndex = resolveMessageIndex(messageId, index, items);
  const previousMessageId = messageIndex > 0 ? items[messageIndex - 1] : undefined;
  const nextMessageId = messageIndex >= 0 && messageIndex < items.length - 1 ? items[messageIndex + 1] : undefined;
  const message = useMessage(messageId);
  const resolvedAuthor = useMessageAuthor(messageId);
  const previousMessage = useMessage(previousMessageId ?? '');
  const nextMessage = useMessage(nextMessageId ?? '');
  const variant = useChatVariant();
  const density = useChatDensity();
  const resolveGroupKey = React.useCallback(candidate => {
    if (!candidate) {
      return null;
    }
    const resolvedMessage = getMessageWithResolvedAuthor(candidate, {
      currentUser: store.parameters.currentUser,
      members: store.parameters.members,
      activeConversation: activeConversation ?? undefined,
      getMessageAuthorId: store.parameters.getMessageAuthorId,
      getMessageAuthorDisplayName: store.parameters.getMessageAuthorDisplayName,
      getMessageAuthorAvatarUrl: store.parameters.getMessageAuthorAvatarUrl
    });
    return groupKey(resolvedMessage ?? candidate);
  }, [activeConversation, groupKey, store.parameters]);
  const prevKey = resolveGroupKey(previousMessage);
  const currentKey = resolveGroupKey(message);
  const nextKey = resolveGroupKey(nextMessage);
  const isFirst = prevKey === null || prevKey !== currentKey;
  const isFirstInList = messageIndex === 0;
  const isLast = nextKey === null || nextKey !== currentKey;
  const isOwnMessage = resolvedAuthor?.isOwnMessage ?? message?.role === 'user';
  const ownerState = React.useMemo(() => ({
    isFirst,
    isFirstInList,
    isLast,
    authorRole: message?.role,
    authorId: resolvedAuthor?.id,
    isOwnMessage,
    variant,
    density
  }), [density, isFirst, isFirstInList, isLast, isOwnMessage, message?.role, resolvedAuthor?.id, variant]);
  const Group = slots?.group ?? 'div';
  const AuthorName = slots?.authorName ?? 'div';
  const groupProps = useSlotProps({
    elementType: Group,
    externalSlotProps: slotProps?.group,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref
    }, getDataAttributes({
      isFirst: ownerState.isFirst,
      isLast: ownerState.isLast,
      authorRole: ownerState.authorRole
    }))
  });
  const authorNameProps = useSlotProps({
    elementType: AuthorName,
    externalSlotProps: slotProps?.authorName,
    ownerState
  });
  const authorLabel = resolvedAuthor?.displayName ?? null;
  const showGroupAuthorName = isFirst && Boolean(authorLabel);
  const authorNameElement = showGroupAuthorName ? /*#__PURE__*/_jsx(AuthorName, _extends({}, authorNameProps, {
    children: authorLabel
  })) : null;

  // In compact mode the author name lives inside the message grid so it shares
  // a row with the avatar. In default mode it sits above the grid.
  const compactAuthorName = variant === 'compact' ? authorNameElement : null;
  const defaultAuthorName = variant !== 'compact' ? authorNameElement : null;
  return /*#__PURE__*/_jsxs(Group, _extends({}, groupProps, {
    children: [defaultAuthorName, children !== undefined ?
    // When custom children are provided (e.g. from DefaultMessageItem),
    // pass `isGrouped` via cloneElement so the inner MessageRoot/ChatMessage
    // receives the correct grouping state for its context.
    React.Children.map(children, child => {
      if (! /*#__PURE__*/React.isValidElement(child) || typeof child.type === 'string' || child.type === React.Fragment || !('messageId' in child.props)) {
        return child;
      }
      const clone = child;
      // In compact mode the author label shares the message's CSS grid. Pass
      // it as a dedicated `groupAuthorName` prop rather than merging it into
      // the child's `children`, so a consumer's own custom `children` stay
      // intact (and render children-only) even in compact mode.
      return /*#__PURE__*/React.cloneElement(clone, _extends({
        isGrouped: !isFirst
      }, compactAuthorName ? {
        groupAuthorName: compactAuthorName
      } : {}));
    }) : /*#__PURE__*/_jsxs(MessageRoot, {
      isGrouped: !isFirst,
      messageId: messageId,
      children: [/*#__PURE__*/_jsx(MessageAvatar, {}), compactAuthorName, /*#__PURE__*/_jsx(MessageContent, {}), /*#__PURE__*/_jsx(MessageMeta, {})]
    })]
  }));
});
if (process.env.NODE_ENV !== "production") MessageGroup.displayName = "MessageGroup";